import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Avatar,
  Divider,
} from '@mui/material';
import { Email, Phone, Business, CalendarMonth } from '@mui/icons-material';
import axios from 'axios';

function SuperAdminAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAdmins() {
      try {
        const response = await axios.get('/api/super-admin/admins');
        setAdmins(response.data);
      } catch (error) {
        console.error('Failed to fetch admins:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchAdmins();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4"
                    sx={{
                      color: "#4F46E5",
                      fontWeight: "bold",
                      mb: 2
                    }}
        >
          Admins Management
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          View and manage all recruiters (admins) in the system.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {admins.map((admin) => (
          <Grid item xs={12} md={6} lg={4} key={admin.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 3 }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    src={admin.profile_image_url}
                    sx={{ width: 60, height: 60, mr: 2, bgcolor: 'primary.main' }}
                  >
                    {admin.first_name?.[0]}{admin.last_name?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {admin.first_name} {admin.last_name}
                    </Typography>
                    <Chip
                      label={admin.has_active_subscription ? 'Subscribed' : 'No Subscription'}
                      color={admin.has_active_subscription ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Email fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{admin.email}</Typography>
                </Box>

                {admin.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Phone fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{admin.phone}</Typography>
                  </Box>
                )}

                {admin.company_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Business fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{admin.company_name}</Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <CalendarMonth fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Joined: {new Date(admin.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button size="small" variant="outlined" fullWidth>
                  View Activity
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {admins.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">
            No admin users found in the system.
          </Typography>
        </Box>
      )}
    </Container>
  );
}

export default SuperAdminAdmins;
